/**
 * economy.js
 * ---------------------------------------------------------------------------
 * Sechste Schwesterdatei der Reihe: identische Logik, eigener Namensraum (ec-).
 *
 * Erzeugt die Papierflieger und Münzen im Hintergrund und lässt sie
 * umherziehen. Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius
 * Pixel, stieben sie auseinander und beruhigen sich danach wieder auf ihr
 * Reisetempo. Kurve, Gitter und Fensterlichter laufen rein über CSS.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Skyline.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initEconomy } from "./economy.js";
 *   initEconomy();
 *
 * Aktiviert wird jedes Element mit der Klasse .ec-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-economy.
 */
export function economy() {
    "use strict";

    var CONFIG = {
        planes:      5,
        coins:       6,
        fleeRadius:  170,
        fleeForce:   1.1,
        maxSpeed:    3.6,
        planeSpeed:  0.8,
        coinSpeed:   0.35,
        wander:      0.04,
        calmDown:    0.012,
        margin:      70,
        planeSize:   [20, 36],
        coinSize:    [12, 22]
    };

    var PLANE =
        '<span class="ec-critter__body">' +
        '<i class="ec-plane ec-plane--l"></i>' +
        '<i class="ec-plane ec-plane--r"></i>' +
        '</span>';

    var COIN = '<span class="ec-critter__body"></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }

    function create(scene) {
        var layer = scene.querySelector(".ec-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isPlane = kind === "plane";
            var range = isPlane ? CONFIG.planeSize : CONFIG.coinSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "ec-critter ec-critter--" + kind;
                el.innerHTML = isPlane ? PLANE : COIN;
                el.style.setProperty("--ec-size", size.toFixed(1) + "px");
                el.style.opacity = isPlane ? rand(0.8, 1).toFixed(2) : rand(0.7, 1).toFixed(2);
                if (!isPlane) {
                    // Münzen kippen versetzt, sonst blinkt das ganze Bild im Takt
                    el.querySelector(".ec-critter__body").style.animationDelay =
                        (-rand(0, 3.6)).toFixed(2) + "s";
                }
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isPlane ? CONFIG.planeSpeed : CONFIG.coinSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isPlane ? size / 1.6 : size,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("plane", small ? Math.ceil(CONFIG.planes / 2) : CONFIG.planes);
        spawn("coin", small ? Math.ceil(CONFIG.coins / 2) : CONFIG.coins);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "plane") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--ec-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.ecReady) return;
        scene.dataset.ecReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--ec-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--ec-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".ec-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-economy");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}